import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router";
import { useArtigoDeChamado } from "../../nucleo/conhecimento";
import { mensagemDoConsole } from "../../nucleo/console";
import { podeNaSessao, useSessao } from "../../nucleo/sessao";
import { Aviso, Botao, Cartao } from "../ui";

interface AcaoDoChamado {
  id: string;
  tipo: "publica" | "interna";
  texto: string;
  autor_email: string | null;
  criada_em: string;
}

function quando(valor: string): string {
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data);
}

/* A opção descreve a resposta (número, quem e quando) SEM repetir o texto: o texto já está na
   conversa, e duplicá-lo na página confunde quem procura a mensagem e leitor de tela. O texto
   escolhido aparece na pré-visualização logo abaixo. */
function rotulo(acao: AcaoDoChamado, numero: number): string {
  return `Resposta ${numero} — ${acao.autor_email ?? "Equipe GEOvendas"}, ${quando(acao.criada_em)}`;
}

/**
 * No chamado interno: transforma UMA resposta pública em rascunho de artigo.
 *
 * Só as respostas públicas são oferecidas — nota interna é conversa da equipe e não é ponto de
 * partida de texto que pode chegar ao cliente (o servidor também recusa). O rascunho nasce
 * "só a equipe"; quem o revisa decide se alarga.
 */
export function ArtigoAPartirDoChamado({
  ticketId,
  acoes,
}: {
  ticketId: string;
  acoes: readonly AcaoDoChamado[];
}) {
  const sessao = useSessao();
  const criar = useArtigoDeChamado();
  const navegar = useNavigate();
  const publicas = acoes.filter((acao) => acao.tipo === "publica");
  const [acaoId, setAcaoId] = useState("");
  const escolhida = publicas.find((acao) => acao.id === acaoId);

  if (!podeNaSessao(sessao.data, "conhecimento:editar") || publicas.length === 0) return null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!acaoId) return;
    criar.mutate(
      { ticket_id: ticketId, acao_id: acaoId },
      { onSuccess: (artigo) => navegar(`/admin/conhecimento/${artigo.id}`) },
    );
  }

  return (
    <Cartao titulo="Base de conhecimento" className="mb-4" data-testid="artigo-a-partir-do-chamado">
      <form onSubmit={enviar}>
        <label className="campo" htmlFor="resposta-para-artigo">
          <span className="campo__rotulo">Transformar uma resposta em artigo</span>
          <select
            id="resposta-para-artigo"
            className="campo__controle"
            value={acaoId}
            onChange={(evento) => setAcaoId(evento.target.value)}
          >
            <option value="">Escolha a resposta</option>
            {publicas.map((acao, indice) => (
              <option value={acao.id} key={acao.id}>
                {rotulo(acao, indice + 1)}
              </option>
            ))}
          </select>
        </label>
        {escolhida ? (
          <blockquote className="texto-preservado nota-rodape mb-3" data-testid="resposta-escolhida">
            {escolhida.texto}
          </blockquote>
        ) : null}
        <p className="nota-rodape mb-3">
          Cria um rascunho visível só para a equipe. Nada vai para o cliente até alguém publicar.
        </p>
        {criar.isError ? (
          <Aviso tom="erro" className="mb-3">
            {mensagemDoConsole(criar.error)}
          </Aviso>
        ) : null}
        <Botao type="submit" variante="fantasma" disabled={!acaoId || criar.isPending}>
          {criar.isPending ? "Criando…" : "Criar rascunho de artigo"}
        </Botao>
      </form>
    </Cartao>
  );
}
